export interface DisposalAccountingCounters {
  active: number;
  released: number;
  disposed: number;
}

export interface DisposalAccountingSnapshot {
  totals: DisposalAccountingCounters;
  byKind: Record<string, DisposalAccountingCounters>;
}

export interface DisposalReleaseOptions {
  disposed?: boolean;
}

export interface DisposalAccountingHandle {
  readonly id: number;
  readonly kind: string;
  released: boolean;
  disposed: boolean;
  release: (options?: DisposalReleaseOptions) => void;
  dispose: () => void;
}

export interface DisposalAccounting {
  register: (kind: string) => DisposalAccountingHandle;
  release: (handle: DisposalAccountingHandle | null | undefined, options?: DisposalReleaseOptions) => void;
  snapshot: () => DisposalAccountingSnapshot;
}

function createCounters(): DisposalAccountingCounters {
  return {
    active: 0,
    released: 0,
    disposed: 0,
  };
}

function cloneCounters(counters: DisposalAccountingCounters): DisposalAccountingCounters {
  return {
    active: Math.max(0, counters.active),
    released: Math.max(0, counters.released),
    disposed: Math.max(0, counters.disposed),
  };
}

function normalizeKind(kind: string): string {
  const normalized = String(kind || "").trim();
  return normalized || "unknown";
}

export function createDisposalAccounting(): DisposalAccounting {
  const totals = createCounters();
  const byKind: Record<string, DisposalAccountingCounters> = {};
  let nextHandleId = 1;

  function countersForKind(kind: string): DisposalAccountingCounters {
    if (!byKind[kind]) {
      byKind[kind] = createCounters();
    }
    return byKind[kind];
  }

  function release(handle: DisposalAccountingHandle | null | undefined, options: DisposalReleaseOptions = {}) {
    if (!handle || handle.released) return;

    const counters = countersForKind(handle.kind);
    counters.active = Math.max(0, counters.active - 1);
    totals.active = Math.max(0, totals.active - 1);
    counters.released += 1;
    totals.released += 1;

    if (options.disposed) {
      counters.disposed += 1;
      totals.disposed += 1;
      handle.disposed = true;
    }

    handle.released = true;
  }

  function register(kind: string): DisposalAccountingHandle {
    const normalizedKind = normalizeKind(kind);
    const counters = countersForKind(normalizedKind);
    counters.active += 1;
    totals.active += 1;

    const handle: DisposalAccountingHandle = {
      id: nextHandleId,
      kind: normalizedKind,
      released: false,
      disposed: false,
      release: (options?: DisposalReleaseOptions) => release(handle, options),
      dispose: () => release(handle, { disposed: true }),
    };

    nextHandleId += 1;
    return handle;
  }

  function snapshot(): DisposalAccountingSnapshot {
    const snapshotByKind: Record<string, DisposalAccountingCounters> = {};
    Object.keys(byKind).sort().forEach((kind) => {
      snapshotByKind[kind] = cloneCounters(byKind[kind]);
    });

    return {
      totals: cloneCounters(totals),
      byKind: snapshotByKind,
    };
  }

  return {
    register,
    release,
    snapshot,
  };
}
